import { useCallback, useEffect, useState } from 'react';

const KEY = 'vw_saved_resources';
const CHANGE_EVENT = 'vw-saved-resources-changed';

// No account needed for this -- schools/unions someone saves while browsing live entirely in
// localStorage (full resource snapshots, not just ids). Naturally cleared if the user clears
// browser data; that's expected, not a bug. The snapshot can go stale if the source listing is
// later deactivated -- SavedResourcesPanel re-checks current status against resource_directory
// before rendering, rather than trusting the snapshot as permanently authoritative.
function readAll() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeAll(items) {
  try { localStorage.setItem(KEY, JSON.stringify(items)); } catch { /* storage unavailable/full -- fail silently */ }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function saveResource(resource) {
  const all = readAll();
  if (all.some(r => r.id === resource.id)) return;
  writeAll([...all, resource]);
}

export function unsaveResource(id) {
  writeAll(readAll().filter(r => r.id !== id));
}

export function clearSavedResources() {
  writeAll([]);
}

// Shared reactive view over the saved list -- every component using this hook (star buttons on
// cards, the "Saved (N)" entry point, the comparison panel) re-renders together on any change,
// including a save/unsave made from a different open tab (the native `storage` event).
export function useSavedResources() {
  const [saved, setSaved] = useState(() => readAll());

  useEffect(() => {
    const onChange = () => setSaved(readAll());
    window.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);

  const isSaved = useCallback((id) => saved.some(r => r.id === id), [saved]);
  const toggle = useCallback((resource) => {
    if (saved.some(r => r.id === resource.id)) unsaveResource(resource.id);
    else saveResource(resource);
  }, [saved]);

  return { saved, isSaved, toggle, clear: clearSavedResources };
}
